import type { TreePickerItem } from '@ipfabric/ui-components/TreePicker/helpers/TreePickerItem';
import { INHERIT_X_WEB_PAGES } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { useLocale } from 'containers/App/LocaleContext';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { Draft } from 'immer';
import { useMemo } from 'react';
import { generateGuid } from 'utils/guid';
import { DefaultOasWebPages } from 'utils/oas/constant';
import { getApiTableTagsMap } from 'utils/oas/getApiTableTagsMap';

type OasEndpointsHook = TreePickerItem<string>[];

const generateParent = (label: string): TreePickerItem<string> => ({
  label,
  key: generateGuid(),
  children: [],
});

const placeByTags = (
  tags: string[],
  tree: Draft<TreePickerItem<string>[]>,
  item: TreePickerItem<string>,
) => {
  const parentIndex = tree.findIndex(({ label }) => label === tags[0]);
  // Fixed noUncheckedIndexedAccess NIM-19408
  const parent =
    parentIndex >= 0 ? tree[parentIndex]! : generateParent(tags[0]!);
  if (tags.length > 1) {
    placeByTags(tags.slice(1), parent.children, item);
  } else {
    parent.children.push(item);
  }

  if (parentIndex === -1) {
    tree.push(parent);
  }
};

export const useOasEndpointsTreePickerItems = (): OasEndpointsHook => {
  const {
    app: { sidebar: sidebarMsg },
  } = useLocale();
  const globalStateCtxProps = useGlobalStateContextSelectors({
    oas: prop('oas'),
  });

  const apiTableTagsMap = useMemo(
    () =>
      globalStateCtxProps.oas
        ? getApiTableTagsMap(
            globalStateCtxProps.oas?.['x-webPages'] ?? DefaultOasWebPages,
            sidebarMsg,
          )
        : {},
    [globalStateCtxProps.oas, sidebarMsg],
  );

  const result = useMemo(() => {
    const items = Object.entries(globalStateCtxProps.oas?.paths ?? {}).reduce<
      TreePickerItem<string>[]
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
    >((acc, [endpoint, methods]) => {
      if (!methods || typeof methods === 'string' || Array.isArray(methods)) {
        return acc;
      }

      for (const [method, item] of Object.entries(methods)) {
        if (
          item !== undefined &&
          typeof item !== 'string' &&
          !Array.isArray(item) &&
          item['x-rbac']?.apiScopeId
        ) {
          const endpointWithMethod = `${method.toUpperCase()} ${endpoint}`;
          const treePickerItem: TreePickerItem = {
            label: item.summary ?? endpointWithMethod,
            key: item['x-rbac'].apiScopeId,
            tooltip: item.description || undefined, // avoid empty string from OAS making render tooltip icon
            description: item.summary ? endpointWithMethod : undefined,
            children: [],
          };

          const tags =
            item['x-tags'] === INHERIT_X_WEB_PAGES
              ? apiTableTagsMap[endpoint]
              : item['x-tags'];

          if (!tags) {
            acc.push(treePickerItem);
          } else {
            placeByTags(tags, acc, treePickerItem);
          }
        }
      }

      return acc;
    }, []);

    // sort list so nested items are at top
    return pipe(
      items,
      array.partition((item) => item.children.length > 0),
      ({ left, right }) => [...right, ...left],
    );
  }, [globalStateCtxProps.oas?.paths, apiTableTagsMap]);

  return result;
};
